// Business dates are time-zone-less ISO dates; timestamps are UTC [X1 BR-X1-20 via 03 §0].
import { z } from 'zod';

declare const businessDateBrand: unique symbol;
export type BusinessDate = string & { readonly [businessDateBrand]: true };

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

export function isBusinessDate(value: unknown): value is BusinessDate {
  if (typeof value !== 'string') return false;
  const m = DATE_RE.exec(value);
  if (!m) return false;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const probe = new Date(Date.UTC(y, mo - 1, d));
  return probe.getUTCFullYear() === y && probe.getUTCMonth() === mo - 1 && probe.getUTCDate() === d;
}

export function toBusinessDate(value: string): BusinessDate {
  if (!isBusinessDate(value)) throw new RangeError(`invalid business date: ${value}`);
  return value;
}

export const businessDateSchema = z
  .string()
  .refine(isBusinessDate, { message: 'invalid date' })
  .transform((v) => v as BusinessDate);

/** ISO dates compare lexically. */
export function compareBusinessDates(a: BusinessDate, b: BusinessDate): -1 | 0 | 1 {
  return a < b ? -1 : a > b ? 1 : 0;
}

/** Calendar date of an instant in a time zone (e.g. `America/Toronto`, BUSINESS_TIME_ZONE). */
export function businessDateOf(instant: Date, timeZone: string): BusinessDate {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(instant);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
  return toBusinessDate(`${get('year')}-${get('month')}-${get('day')}`);
}

export function addDays(date: BusinessDate, days: number): BusinessDate {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  const t = new Date(Date.UTC(y, m - 1, d + days));
  return t.toISOString().slice(0, 10) as BusinessDate;
}
